import { Box, CircularProgress } from "@material-ui/core";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { ResourceType } from "enums/videoType";
import { FC, Fragment, ReactNode, useState } from "react";
import ReactPlayer from "react-player";
import { useSelector } from "react-redux";
import {
  FacebookEmbed,
  InstagramEmbed,
  PinterestEmbed,
  TikTokEmbed,
  XEmbed,
} from "react-social-media-embed";
import { GetConfigData } from "store/config/selectors";
import { UploadType } from "store/url-builder";
import {
  getLinkedInEmbedUrl,
  isFacebookReel,
  isFacebookVideo,
  isFacebookShareUrl,
  getFacebookVideoEmbedUrl,
  isInstagram,
  isLinkedIn,
  isPinterest,
  isTikTok,
  isTwitter,
} from "utils";
import style from "./style.module.scss";

import ReactImage from "react-image-shimmer";

export interface FileReaderProps {
  file: UploadType;
  videoConfig?: {
    controls: boolean;
  };
}

let gcd: any = (a: number, b: number) => {
  return b ? gcd(b, a % b) : a;
};

let aspectRatio = (width: number, height: number) => {
  var divisor = gcd(width, height);
  return width / divisor + ":" + height / divisor;
};

export const FileReader: FC<FileReaderProps> = (props) => {
  const confData = useSelector(GetConfigData);
  const filePath =
    props.file.actionType === ResourceType.Regular
      ? confData.filesBaseUrl +
        confData.filesBasePath +
        "/" +
        props.file.fileName
      : props.file.fileUrl;

  const [bgImage, setBgImage] = useState<null | ReactNode>(null);

  if (props.file.mimeType.includes("image")) {
    if (filePath) {
      const img = new Image();
      img.src = filePath;
      img.onload = () => {
        if (aspectRatio(img.width, img.height) !== "1:1")
          setBgImage(<img src={img.src} alt="" className="slideBgImage" />);
      };
    }

    return (
      <Fragment>
        <ReactImage
          src={filePath || "https://"}
          fallback={
            <div className={style.loader}>
              <CircularProgress />
            </div>
          }
          nativeImgProps={{
            alt: props.file.fileName,
            width: "100%",
            height: "auto",
            style: {
              objectFit: "contain",
            },
          }}
        />
        {props.videoConfig?.controls && bgImage}
      </Fragment>
    );
  }

  if (
    props.file.mimeType.includes("video") ||
    (!props.file.mimeType.includes("image") &&
      ![
        "png",
        "jpg",
        "jpeg",
        "pdf",
        "svg",
        "gif",
        "ai",
        "eps",
        "raw",
        "indd",
        "tiff",
      ].includes(
        props.file?.fileUrl?.slice(
          props.file?.fileUrl!.lastIndexOf(".") + 1,
          props.file?.fileUrl?.length,
        ) || "",
      ))
  ) {
    if (!filePath) return null;

    const isInstagramUrl = isInstagram(filePath);
    const isFacebookReelUrl = isFacebookReel(filePath);
    const isFacebookVideoUrl = isFacebookVideo(filePath);
    const isFacebookShareUrlDetected = isFacebookShareUrl(filePath);
    const isFacebookUrl = isFacebookReelUrl || isFacebookVideoUrl || isFacebookShareUrlDetected;
    const isTikTokUrl = isTikTok(filePath);
    const isTwitterUrl = isTwitter(filePath);
    const isLinkedInUrl = isLinkedIn(filePath);
    const isPinterestUrl = isPinterest(filePath);

    const isAutoEmbed =
      isTwitterUrl || isPinterestUrl || isInstagramUrl || isFacebookUrl;

    const playerWrapperClassName = isAutoEmbed
      ? `${style.playerWrapper} ${style.playerWrapperAuto}`
      : style.playerWrapper;

    return (
      <div
        className={playerWrapperClassName}
        style={{
          pointerEvents: props.videoConfig?.controls ? "all" : "none",
        }}
      >
        {isInstagramUrl ? (
          <div
            className=""
            style={{
              width: "100%",
              height: "auto",
              justifyContent: "center",
              // transform: "scale(.8)",
            }}
          >
            <InstagramEmbed url={filePath} width={328} />
          </div>
        ) : isTikTokUrl ? (
          <div className="social-embed-wrapper social-embed-wrapper--aspect">
            <TikTokEmbed url={filePath} width={325} />
          </div>
        ) : isTwitterUrl ? (
          <div
            className=""
            style={{
              width: "100%",
              height: "auto",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <XEmbed url={filePath} width={325} />
          </div>
        ) : isLinkedInUrl ? (
          <div className="social-embed-wrapper social-embed-wrapper--auto social-embed-wrapper--linkedin">
            <iframe
              title="LinkedIn embed"
              src={getLinkedInEmbedUrl(filePath)}
              width="100%"
              height="700"
              allowFullScreen
            />
          </div>
        ) : isPinterestUrl ? (
          <div
            className=""
            style={{
              width: "100%",
              height: "auto",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <PinterestEmbed url={filePath} width={325} />
          </div>
        ) : isFacebookShareUrlDetected ? (
          <div
            className=""
            style={{ width: "100%", height: "auto", justifyContent: "center" }}
          >
            <iframe
              title="Facebook share video"
              src={getFacebookVideoEmbedUrl(filePath)}
              width="100%"
              height="500"
              style={{ border: "none", overflow: "hidden" }}
              scrolling="no"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            />
          </div>
        ) : isFacebookUrl ? (
          <div
            className=""
            style={{ width: "100%", height: "auto", justifyContent: "center" }}
          >
            <FacebookEmbed url={filePath} width={325} />
          </div>
        ) : (
          <ReactPlayer
            url={filePath}
            width="100%"
            height="auto"
            {...props.videoConfig}
          />
        )}
      </div>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <InsertDriveFileIcon fontSize="large" />
        <div>
          <small>{props.file.mimeType}</small>
        </div>
      </Box>
    </Box>
  );
};
